import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

export const alertVariants = cva(
  'relative w-full rounded-xl border p-4 text-sm [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground [&>svg~*]:pl-7',
  {
    variants: {
      variant: {
        default: 'bg-background text-foreground border-border',
        info: 'bg-blue-500/10 text-blue-900 dark:text-blue-200 border-blue-500/20 [&>svg]:text-blue-600',
        warning: 'bg-amber-500/10 text-amber-900 dark:text-amber-200 border-amber-500/20 [&>svg]:text-amber-600',
        error: 'bg-destructive/10 text-destructive border-destructive/20 [&>svg]:text-destructive',
        success: 'bg-emerald-500/10 text-emerald-900 dark:text-emerald-200 border-emerald-500/20 [&>svg]:text-emerald-600',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export const Alert = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants> & {
    type?: 'info' | 'warning' | 'error' | 'success';
    message?: React.ReactNode;
    description?: React.ReactNode;
    showIcon?: boolean;
    icon?: React.ReactNode;
    action?: React.ReactNode;
  }
>(({ className, variant, type, message, description, showIcon: _showIcon, icon, action, children, ...props }, ref) => {
  const resolvedVariant = (type ?? variant ?? 'default') as VariantProps<typeof alertVariants>['variant'];

  return (
    <div
      ref={ref}
      role="alert"
      className={cn(alertVariants({ variant: resolvedVariant }), className)}
      {...props}
    >
      <div className="flex items-start justify-between gap-3">
        {icon && <div className="shrink-0">{icon}</div>}
        <div className="flex-1">
          {message && <h5 className="font-semibold leading-tight tracking-tight mb-1">{message}</h5>}
          {description && <div className="text-sm opacity-90 leading-normal">{description}</div>}
          {children}
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </div>
    </div>
  );
});
Alert.displayName = 'Alert';

export const AlertTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h5
    ref={ref}
    className={cn('mb-1 font-semibold leading-none tracking-tight', className)}
    {...props}
  />
));
AlertTitle.displayName = 'AlertTitle';

export const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('text-sm [&_p]:leading-relaxed opacity-90', className)}
    {...props}
  />
));
AlertDescription.displayName = 'AlertDescription';
